import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useInView } from 'framer-motion';
import apps from '../../data/apps';
import AppCard from './AppCard';

function Counter({ value, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf = 0;
    let start = performance.now();
    const duration = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setCount(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function Main({ showHero = true, excludeCategories = [], appsSectionTitle = '' }) {
  const filteredApps = apps.filter(app => !excludeCategories.includes(app.category));
  const groupedApps = filteredApps.reduce((acc, app) => {
    acc[app.category] = acc[app.category] || [];
    acc[app.category].push(app);
    return acc;
  }, {});

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { damping: 25, stiffness: 200 });
  const springY = useSpring(mouseY, { damping: 25, stiffness: 200 });
  useEffect(() => {
    const onMove = (e) => { mouseX.set(e.clientX); mouseY.set(e.clientY); };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [mouseX, mouseY]);

  const categories = Object.keys(groupedApps);

  return (
    <div className="relative">
      <motion.div style={{ x: springX, y: springY }} className="pointer-events-none fixed -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full opacity-[0.05] blur-[80px] z-0 hidden lg:block">
        <div className="w-full h-full rounded-full bg-gradient-to-r from-sky-500 via-cyan-400 to-blue-600" />
      </motion.div>

      {showHero && (
        <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-16 overflow-hidden">
          <div className="mx-auto max-w-[1280px] px-6 lg:px-8">
            <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <div className="inline-flex items-center gap-2 rounded-full glass px-1.5 py-1 pr-4 text-xs">
                  <span className="rounded-full bg-white text-black text-[10px] font-bold tracking-widest px-2.5 py-1">NEW</span>
                  <span className="text-white/70 hidden sm:inline">{filteredApps.length} apps • {categories.length} categories • Made in Washington, USA</span>
                  <span className="sm:hidden text-white/70">{filteredApps.length} apps • Washington, USA</span>
                  <span className="text-white/40">→</span>
                </div>
              </motion.div>

              <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="mt-8 text-[42px] sm:text-[56px] lg:text-[72px] font-[650] leading-[0.92] tracking-[-0.04em]">
                <span className="block text-white">Discover Apps</span>
                <span className="block bg-gradient-to-r from-sky-300 via-cyan-300 to-blue-400 bg-clip-text text-transparent">for Every Need</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }} className="mt-6 text-[17px] lg:text-[19px] leading-7 text-white/55 max-w-[640px]">
                From education to utilities, explore our selection of everyday useful apps. Crafted by Draunt — premium, fast, and loved on the App Store.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <a href="#apps" className="group inline-flex items-center gap-2 rounded-full bg-white text-black font-medium text-[14.5px] px-8 h-[48px] hover:bg-white/90 transition shadow-[0_12px_32px_rgba(255,255,255,0.18)]">
                  Browse All Apps <span className="group-hover:translate-x-0.5 transition">→</span>
                </a>
              </motion.div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 flex flex-wrap items-center justify-center gap-5 text-xs text-white/40">
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)] animate-pulse" /> Available on App Store</span>
                <span className="h-3 w-px bg-white/10" />
                <span>Washington, USA</span>
                <span className="h-3 w-px bg-white/10" />
                <span className="flex items-center gap-1.5">★★★★★ <span className="text-white/60">Top Rated</span></span>
              </motion.div>
              <div className="mt-10 grid grid-cols-3 gap-6 border-t border-white/10 pt-6 w-full max-w-[520px]">
                {[
                  { v: filteredApps.length, s: '', l: 'Apps shipped' },
                  { v: categories.length, s: '', l: 'Categories' },
                  { v: 100, s: '%', l: 'Useful & free' },
                ].map((s) => (
                  <div key={s.l} className="text-center">
                    <div className="text-[28px] font-semibold tracking-tight text-white"><Counter value={s.v} suffix={s.s} /></div>
                    <div className="text-xs text-white/45">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="border-y border-white/[0.06] bg-white/[0.01] backdrop-blur">
        <div className="mx-auto max-w-[1280px] px-6 lg:px-8 py-6 flex flex-col lg:flex-row items-center gap-6">
          <div className="text-[11px] tracking-[0.16em] text-white/30 font-medium whitespace-nowrap">AVAILABLE ON THE APP STORE</div>
          <div className="flex-1 overflow-hidden relative w-full">
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#040a14] to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#040a14] to-transparent z-10" />
            <div className="flex w-max animate-[marquee_22s_linear_infinite] gap-10 items-center opacity-60 hover:opacity-90 transition">
              {[...Array(2)].map((_, dup) => (
                <div key={dup} className="flex items-center gap-10 shrink-0">
                  {filteredApps.map((a) => (
                    <span key={a.name + dup} className="text-[15px] font-semibold tracking-tight text-white/70 flex items-center gap-2"><img src={a.icon} alt="" className="w-6 h-6 rounded-lg border border-white/10" />{a.name}</span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="apps" className="mx-auto max-w-[1280px] px-6 lg:px-8 py-20 lg:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] text-sky-300 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-sky-400" /> COLLECTION</div>
            <h2 className="mt-3 text-[36px] lg:text-[48px] font-semibold tracking-[-0.03em] leading-[0.95] text-white">
              {appsSectionTitle || 'Crafted apps'} <br className="hidden lg:block" /><span className="text-white/40">for everyday life.</span>
            </h2>
          </div>
          <p className="max-w-[420px] text-[15px] leading-6 text-white/50">Tap any card to download on the App Store. Each app is designed to be fast, simple, and genuinely useful.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map(cat => (
            <a key={cat} href={`#${cat.toLowerCase().replace(/\s+/g,'-')}`} className="rounded-full glass px-4 py-2 text-xs font-medium text-white/70 hover:text-white hover:bg-white/10 transition">{cat}</a>
          ))}
        </div>

        {Object.entries(groupedApps).map(([category, appsInCategory]) => (
          <div id={category.toLowerCase().replace(/\s+/g, '-')} key={category} className="mb-16 scroll-mt-24">
            <div className="flex items-center gap-3 mb-6">
              <h3 className="text-[13px] tracking-[0.14em] font-semibold text-white/60">{category.toUpperCase()}</h3>
              <div className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
              <span className="text-xs text-white/30">{appsInCategory.length} apps</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {appsInCategory.map((app, i) => (
                <AppCard key={app.name} app={app} index={i} />
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="mx-auto max-w-[1280px] px-6 lg:px-8 pb-20">
        <div className="relative rounded-[32px] overflow-hidden p-[1px] bg-gradient-to-br from-sky-500/40 via-cyan-500/25 to-blue-600/40">
          <div className="relative rounded-[31px] overflow-hidden bg-[#070e1e] px-6 lg:px-12 py-12 lg:py-16">
            <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(600px circle at 80% 20%, rgba(14,165,233,0.28), transparent 60%), radial-gradient(500px circle at 10% 80%, rgba(6,182,214,0.22), transparent 60%)' }} />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-[30px] lg:text-[42px] font-semibold tracking-[-0.03em] leading-[0.95] text-white">Get the apps <br /><span className="bg-gradient-to-r from-sky-300 to-cyan-300 bg-clip-text text-transparent">you’ll actually use</span></h2>
                <p className="mt-4 text-white/55 text-[15px] leading-6 max-w-[520px]">Join thousands who use Draunt apps every day. One tap to the App Store — fast, free, and focused on what matters.</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a href="#apps" className="rounded-full bg-white text-black font-medium px-7 h-[48px] inline-flex items-center hover:bg-white/90 transition">Browse All Apps →</a>
                  <a href="/contact" className="rounded-full border border-white/15 bg-white/5 backdrop-blur px-6 h-[48px] inline-flex items-center text-sm font-medium hover:bg-white/10 transition text-white">Contact Us</a>
                </div>
                <div className="mt-4 text-xs text-white/35">Washington, USA • Trusted on the App Store</div>
              </div>
              <div className="relative lg:pl-8">
                <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 grid grid-cols-3 gap-3">
                  {apps.slice(0,6).map(a => (
                    <div key={a.name} className="glass rounded-2xl p-3 flex flex-col items-center text-center">
                      <img src={a.icon} alt="" className="w-12 h-12 rounded-xl" />
                      <div className="mt-2 text-xs font-medium text-white leading-tight">{a.name}</div>
                      <div className="text-[11px] text-white/40">{a.category}</div>
                    </div>
                  ))}
                </div>
                <div className="absolute -right-2 -top-4 lg:right-4 hidden sm:flex glass rounded-2xl px-3 py-2.5 gap-2 items-center shadow-xl">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs font-medium text-white">App Store • Live</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
